import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { categorySchema, merchantSchema, type Category, type Merchant } from '@shared/schemas/expenses';

export const taxonomyKeys = {
  all: ['taxonomy'] as const,
  categories: () => [...taxonomyKeys.all, 'categories'] as const,
  merchantSearch: (q: string) => [...taxonomyKeys.all, 'merchants', 'search', q] as const,
  merchantById: (id: string) => [...taxonomyKeys.all, 'merchants', id] as const,
};

// ==================== Categories ====================

/**
 * Las categorías del desplegable.
 *
 * **Esto es una consulta a la tabla, no una RPC**, y ahí está la asimetría con
 * los comercios: aquéllos pasan por `search_merchants`, que filtra por dentro,
 * y éstas se listan a pelo. Por eso el filtro de lo que crean las cuentas de
 * pruebas tiene que estar **aquí**: `20260815121023` hace que esas filas nazcan
 * `pending`, pero este listado acepta las `pending` a propósito —una categoría
 * recién creada por alguien tiene que poder usarse antes de moderarse—, así que
 * nacer pendiente no las tapa, sólo las cambia de mitad. Ver #33 y ADR-0060.
 */
export async function fetchAllCategories(): Promise<Category[]> {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .in('moderation_status', ['approved', 'pending'])
    .eq('is_test', false)
    .order('is_predefined', { ascending: false })
    .order('name', { ascending: true });

  if (error) {
    logger.error('fetchAllCategories failed', { code: error.code });
    throw new Error(error.message);
  }
  return (data ?? []).map((row) => categorySchema.parse(row));
}

// ==================== Merchants ====================

/**
 * Búsqueda fuzzy de comercios usando pg_trgm.
 * El operador % de similarity con índice GIN devuelve matches "cercanos".
 * Se ordenan por similarity desc y frecuencia (simplificada por created_at por ahora).
 */
export async function searchMerchants(query: string, limit = 10): Promise<Merchant[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  // Fuzzy matching real vía RPC search_merchants (pg_trgm similarity + prefix match).
  // Tolera typos como "mergadona" → "mercadona".
  const { data, error } = await supabase.rpc('search_merchants', {
    p_query: q,
    p_limit: limit,
  });

  if (error) {
    logger.error('searchMerchants failed', { code: error.code });
    throw new Error(error.message);
  }
  // La RPC devuelve una columna extra `score` — la ignoramos con parse
  return ((data ?? []) as Merchant[]).map((row) => merchantSchema.parse(row));
}
